/** @jsxImportSource @emotion/react */
import { formatDate } from '@kaiizen/kai-presenters';
import { ApiPropertyProject } from '@kaiizen/kai-pro-api';

import {
  ChangeOrderIcon,
  CloseOutReviewIcon,
  PunchListIcon,
  ScopeIcon,
} from '../../Icons/AllIcons';
import { CAUGHT_UP_ICON_STYLE, SEARCH_ICON_STYLE } from './dashboardStyles';

const PENDING_SCAN_LIFECYCLE_STATUSES = new Set([
  'pendingReview',
  'inReview',
  'correctionsRequested',
]);

export const getDateValue = (value?: Date | string | null) => {
  if (!value) return undefined;
  const parsed = value instanceof Date ? value : new Date(value);
  const time = parsed.getTime();
  return Number.isNaN(time) ? undefined : parsed;
};

export const getDateNumber = (value?: Date | string | null) => getDateValue(value)?.getTime() ?? 0;

export const isProjectArchived = (project: ApiPropertyProject) => !!project.archivedAt;

export const projectNeedsAttention = (project: ApiPropertyProject) => {
  const dueDate = getDateValue(project.latestScan?.customerMetadata?.dueDate);
  if (project.status !== 'active') return false;

  if (PENDING_SCAN_LIFECYCLE_STATUSES.has(project.latestScan?.scanLifecycleStatus ?? '')) return true;
  if (!dueDate) return false;

  const today = new Date();
  const dueUtc = Date.UTC(dueDate.getUTCFullYear(), dueDate.getUTCMonth(), dueDate.getUTCDate());
  const todayUtc = Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate());
  return dueUtc < todayUtc;
};

export const getArtifactPresentation = (project: ApiPropertyProject) => {
  const scanType = project.latestScan?.scanType ?? project.latestApprovedScan?.scanType ?? project.firstScan?.scanType;

  if (scanType === 'changeRequest') {
    return { label: 'Change Order', color: '#E36B23', icon: <ChangeOrderIcon size={11} color='#E36B23' /> };
  }
  if (scanType === 'progressCompleted') {
    return { label: 'Close Out', color: '#2D9B5E', icon: <CloseOutReviewIcon size={11} color='#2D9B5E' /> };
  }
  if (scanType === 'progressRework') {
    return { label: 'Punch List', color: '#2878B8', icon: <PunchListIcon size={11} color='#2878B8' /> };
  }

  return { label: 'Scope', color: '#7E4DDB', icon: <ScopeIcon size={11} color='#7E4DDB' /> };
};

export const getStatusPresentation = (project: ApiPropertyProject) => {
  const lifecycle = project.latestScan?.scanLifecycleStatus ?? project.latestApprovedScan?.scanLifecycleStatus;

  if (lifecycle === 'pendingReview') {
    return {
      label: 'Pending Review',
      color: '#B82C2C',
      icon: (
        <svg width='12' height='12' viewBox='0 0 12 12' fill='none' aria-hidden>
          <path
            d='M5.3999 11.8496C8.43747 11.8496 10.8999 9.38718 10.8999 6.34961C10.8999 3.31204 8.43747 0.849609 5.3999 0.849609C2.36234 0.849609 -0.100098 3.31204 -0.100098 6.34961C-0.100098 9.38718 2.36234 11.8496 5.3999 11.8496Z'
            fill='#B82C2C'
          />
          <path d='M4.0249 7.72461V4.97461' stroke='#FEEEEE' strokeWidth='1.5' strokeLinecap='round' strokeLinejoin='round' />
          <path d='M6.7749 7.72461V4.97461' stroke='#FEEEEE' strokeWidth='1.5' strokeLinecap='round' strokeLinejoin='round' />
        </svg>
      ),
    };
  }

  if (lifecycle === 'inProgress' || lifecycle === 'created') {
    return {
      label: 'In Progress',
      color: '#2878B8',
      icon: (
        <svg width='11' height='11' viewBox='0 0 11 11' fill='none' aria-hidden>
          <path
            d='M5.44995 11C8.48752 11 10.95 8.53757 10.95 5.5C10.95 2.46243 8.48752 0 5.44995 0C2.41238 0 -0.0500488 2.46243 -0.0500488 5.5C-0.0500488 8.53757 2.41238 11 5.44995 11Z'
            fill='#2878B8'
          />
          <path d='M5.44995 2.75V5.5L6.97773 5.80556' stroke='#EDF5FD' strokeWidth='1.5' strokeLinecap='round' strokeLinejoin='round' />
        </svg>
      ),
    };
  }

  if (project.status === 'completed') {
    return { label: 'Completed', color: '#2D9B5E', icon: <CloseOutReviewIcon size={11} color='#2D9B5E' /> };
  }

  return { label: 'Active', color: '#69635B', icon: null };
};

export const SearchIcon = () => (
  <svg css={SEARCH_ICON_STYLE} viewBox='0 0 24 24' fill='none' aria-hidden>
    <path
      d='M10.9995 18C14.8655 18 17.9995 14.866 17.9995 11C17.9995 7.13401 14.8655 4 10.9995 4C7.13352 4 3.99951 7.13401 3.99951 11C3.99951 14.866 7.13352 18 10.9995 18Z'
      stroke='#1D1D1D'
      strokeWidth='1.5'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
    <path d='M15.9995 16L19.9995 20' stroke='#1D1D1D' strokeWidth='1.5' strokeLinecap='round' strokeLinejoin='round' />
  </svg>
);

export const SearchCloseIcon = () => (
  <svg width='16' height='16' viewBox='0 0 16 16' fill='none' aria-hidden>
    <path d='M12 4L4 12' stroke='#1D1D1D' strokeLinecap='round' strokeLinejoin='round' />
    <path d='M4 4L12 12' stroke='#1D1D1D' strokeWidth='1.5' strokeLinecap='round' strokeLinejoin='round' />
  </svg>
);

export const CaughtUpIcon = () => (
  <svg css={CAUGHT_UP_ICON_STYLE} viewBox='0 0 64 64' fill='none' aria-hidden>
    <path
      d='M32 63C49.1208 63 63 49.1208 63 32C63 14.8792 49.1208 1 32 1C14.8792 1 1 14.8792 1 32C1 49.1208 14.8792 63 32 63Z'
      stroke='#2E8C5C'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
    <path d='M20.3745 32L28.1245 39.75L43.6245 24.25' stroke='#2E8C5C' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
  </svg>
);

export const formatDashboardDate = (value?: Date | string | null) => value ? formatDate(value) : '--';
